import React, { useEffect, useMemo, useState, useCallback } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Toaster } from "./components/ui/sonner";
import {
  Loader2, CheckCircle2, Download, ExternalLink, Plus,
  Save, AlertTriangle, Send, Mail, X, DollarSign, FileText, Paperclip,
  ChevronLeft, ChevronRight, GripVertical,
} from "lucide-react";
import { DndContext, closestCenter, TouchSensor, PointerSensor, useSensor, useSensors } from "@dnd-kit/core";
import { SortableContext, verticalListSortingStrategy, useSortable, arrayMove } from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Design "Renewal by Andersen Atlanta – Install Labor Bill" (verde escuro, coluna única)
const C = {
  bg: "#f3f6f5", dark: "#1d5a43", green: "#2e7457", head: "#edf2ef", border: "#d3dcd7", line: "#e4eae6",
  text: "#1f2a25", muted: "#6b7a72", inputBorder: "#b7c2bc", disabledBg: "#e9eeeb", disabledText: "#7b867f", grey: "#e3e9e5",
};
const GREEN = C.green;
const FONT = '"Helvetica Neue", Helvetica, Arial, sans-serif';
const parseMoney = (v) => { const n = parseFloat(String(v ?? "").replace(/[^0-9.\-]/g, "")); return Number.isFinite(n) ? n : 0; };
const fmtDate = (raw) => {
  const m = String(raw || "").match(/^(\d{4})-(\d{2})-(\d{2})/);
  return m ? `${m[2]}/${m[3]}/${m[1]}` : (raw || "");
};

const money = (v) =>
  `$${Number(v || 0).toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
const SortableRecipient = ({ r, checked, onToggle }) => {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id: r.id });
  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.75 : 1,
    boxShadow: isDragging ? "0 6px 16px rgba(0,0,0,0.18)" : "none",
  };
  return (
    <div ref={setNodeRef} style={style} className="flex items-center gap-2 rounded-lg border border-slate-200 px-3 py-2.5 bg-white hover:bg-slate-50" data-testid={`fom-rec-${r.id}`}>
      <span
        {...attributes}
        {...listeners}
        className="shrink-0 text-slate-400 hover:text-slate-600 cursor-grab active:cursor-grabbing"
        style={{ touchAction: "none" }}
        title="Hold 2s and drag to reorder"
        data-testid={`fom-rec-drag-${r.id}`}
      >
        <GripVertical className="h-5 w-5" />
      </span>
      <input type="checkbox" checked={checked} onChange={onToggle} className="h-5 w-5" style={{ accentColor: GREEN }} data-testid={`fom-rec-check-${r.id}`} />
      <div className="min-w-0 flex-1 cursor-pointer" onClick={onToggle}>
        <p className="text-[13px] text-slate-800 truncate">{r.name || r.email}</p>
        <p className="text-[11px] text-slate-400 truncate">{r.email}</p>
      </div>
      {r.is_finance && (
        <span className="text-[10px] font-semibold px-2 py-0.5 rounded-full flex items-center gap-1 shrink-0" style={{ color: "#4d7a12", background: "#eef7e0" }}>
          <DollarSign className="h-3 w-3" />Finance
        </span>
      )}
    </div>
  );
};

const num = (v) => {
  const n = Number(v || 0);
  return n ? n.toLocaleString("en-US", { maximumFractionDigits: 2 }) : "0";
};
const fmtSentAt = (iso) => {
  try {
    const d = new Date(iso);
    const date = d.toLocaleDateString('en-US', { timeZone: 'America/New_York', month: '2-digit', day: '2-digit', year: 'numeric' });
    const time = d.toLocaleTimeString('en-US', { timeZone: 'America/New_York', hour: "numeric", minute: "2-digit" });
    return `${date} at ${time}`;
  } catch (e) {
    return iso;
  }
};
const fmtSize = (b) => (b > 1024 * 1024 ? `${(b / 1024 / 1024).toFixed(1)} MB` : `${Math.max(1, Math.round(b / 1024))} KB`);
const compressImage = (file) =>
  new Promise((resolve) => {
    if (!file.type || !file.type.startsWith("image/") || /heic|heif/i.test(file.type)) return resolve(file);
    const img = new Image();
    const url = URL.createObjectURL(file);
    img.onload = () => {
      const maxDim = 1600;
      let { width, height } = img;
      if (width > maxDim || height > maxDim) {
        const r = Math.min(maxDim / width, maxDim / height);
        width = Math.round(width * r);
        height = Math.round(height * r);
      }
      const canvas = document.createElement("canvas");
      canvas.width = width;
      canvas.height = height;
      canvas.getContext("2d").drawImage(img, 0, 0, width, height);
      canvas.toBlob(
        (blob) => {
          URL.revokeObjectURL(url);
          resolve(blob && blob.size < file.size ? blob : file);
        },
        "image/jpeg",
        0.72
      );
    };
    img.onerror = () => { URL.revokeObjectURL(url); resolve(file); };
    img.src = url;
  });

const getToken = () => {
  const m = window.location.pathname.match(/\/fom\/([^/?#]+)/);
  return m ? m[1] : null;
};


// ── Primitivas visuais do layout ─────────────────────────────────────────────
const inputStyle = { width: "100%", height: 40, border: `1px solid ${C.inputBorder}`, borderRadius: 4, padding: "0 10px", fontSize: 13, background: "#fff", color: C.text, boxSizing: "border-box" };
const selectStyle = { ...inputStyle, fontWeight: 700, appearance: "auto" };
const qtyStyle = { width: "100%", maxWidth: 96, height: 34, border: `1px solid ${C.inputBorder}`, borderRadius: 4, padding: "0 6px 0 8px", fontSize: 13, textAlign: "right", background: "#fff", color: C.text, boxSizing: "border-box" };
const btnBase = { display: "inline-flex", alignItems: "center", justifyContent: "center", gap: 8, border: "none", borderRadius: 6, padding: "10px 18px", fontSize: 13, fontWeight: 700, cursor: "pointer", whiteSpace: "nowrap", textDecoration: "none" };
const btnGrey = { ...btnBase, background: C.grey, color: C.text };
const btnDark = { ...btnBase, background: C.dark, color: "#fff" };

const Card = ({ children, testid, style }) => (
  <div style={{ background: "#fff", border: `1px solid ${C.border}`, borderRadius: 6, boxShadow: "0 1px 3px rgba(20,40,30,0.08)", marginBottom: 14, overflow: "hidden", ...style }} data-testid={testid}>
    {children}
  </div>
);
const SectionBar = ({ title }) => (
  <div style={{ background: C.green, color: "#fff", fontWeight: 700, fontSize: 12, letterSpacing: 0.2, textTransform: "uppercase", padding: "10px 16px" }}>{title}</div>
);
const Field = ({ label, sub, children }) => (
  <label style={{ display: "block", minWidth: 0 }}>
    <span style={{ display: "block", fontWeight: 700, fontSize: 13, color: "#2b3833", lineHeight: 1.25 }}>{label}</span>
    {sub ? <span style={{ display: "block", fontSize: 12, color: C.muted, lineHeight: 1.25, marginTop: 1 }}>{sub}</span> : null}
    <span style={{ display: "block", marginTop: 6 }}>{children}</span>
  </label>
);
// Job Information = mesmo visual de campo, somente leitura (comportamento atual do Supervisor)
const ReadOnly = ({ value, placeholder, testid }) => (
  <input value={value || ""} placeholder={placeholder || ""} readOnly tabIndex={-1} style={{ ...inputStyle, background: C.disabledBg, color: value ? C.text : C.disabledText, fontWeight: 600, cursor: "default" }} data-testid={testid} />
);
const Total = ({ label, value, testid }) => (
  <div style={{ textAlign: "center", padding: "12px 8px 14px" }}>
    <div style={{ fontSize: 12, color: C.muted, textTransform: "uppercase", letterSpacing: 0.3 }}>{label}</div>
    <div style={{ fontSize: 17, fontWeight: 700, marginTop: 2 }} data-testid={testid}>{value}</div>
  </div>
);

// ── Seção de itens (Labor Item | Rate | Quantity | Total) ────────────────────
function ItemSection({ title, items, onQty }) {
  return (
    <Card>
      <SectionBar title={title} />
      <table className="fom-items">
        <colgroup><col /><col className="c-rate" /><col className="c-qty" /><col className="c-total" /></colgroup>
        <thead><tr><th>Labor Item</th><th>Rate</th><th>Quantity</th><th>Total</th></tr></thead>
        <tbody>
          {items.map((it) => {
            const total = (Number(it.qty) || 0) * (Number(it.unit_price) || 0);
            return (
              <tr key={it.key} data-testid={`fom-item-${it.key}`}>
                <td>{it.description}</td>
                <td>{money(it.unit_price)}</td>
                <td>
                  <input type="number" inputMode="numeric" min="0" value={it.qty} onChange={(e) => onQty(it.key, e.target.value)} onFocus={(e) => e.target.select()} style={qtyStyle} data-testid={`fom-qty-${it.key}`} />
                </td>
                <td className="fom-total">{money(total)}</td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </Card>
  );
}

export default function FomBillEditor() {
  const token = getToken();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [header, setHeader] = useState({});
  const [left, setLeft] = useState([]);
  const [right, setRight] = useState([]);
  const [notes, setNotes] = useState("");
  const [legacyConverted, setLegacyConverted] = useState(false);
  const [sigOptions, setSigOptions] = useState([]);
  const [fom, setFom] = useState({
    fom_approved_labor: false,
    fom_confirmed_mileage: false,
    fom_signature: "",
    miles_driven: "",
    materials_cost: "",
    mileage_cost: "",
    lodging_cost: "",
    additional_labor_cost: "",
  });
  const [submittedBy, setSubmittedBy] = useState("");
  const [saving, setSaving] = useState(false);
  const [done, setDone] = useState(false);
  const [pdfBlobUrl, setPdfBlobUrl] = useState(null);
  const [pdfName, setPdfName] = useState("Labor Bill.pdf");
  const [recipients, setRecipients] = useState([]);
  const [selectedRecIds, setSelectedRecIds] = useState([]);
  const [extraEmails, setExtraEmails] = useState([]);
  const [newEmail, setNewEmail] = useState("");
  const [showSend, setShowSend] = useState(false);
  const [sending, setSending] = useState(false);
  const [sentTo, setSentTo] = useState(null);
  const [sentInfo, setSentInfo] = useState(null);
  const [attachments, setAttachments] = useState([]);
  const [uploadingAttach, setUploadingAttach] = useState(false);
  const [lightbox, setLightbox] = useState(null);

  const addKeys = (sections, prefix) =>
    sections.map((s, si) => ({ ...s, items: s.items.map((it, ii) => ({ ...it, key: `${prefix}-${si}-${ii}` })) }));

  const load = useCallback(async () => {
    if (!token) {
      setError("Invalid link.");
      setLoading(false);
      return;
    }
    try {
      const { data } = await axios.get(`${API}/public/fom-bill/${token}`);
      setHeader(data.header || {});
      setLeft(addKeys(data.left || [], "L"));
      setRight(addKeys(data.right || [], "R"));
      setLegacyConverted(!!data.legacy_converted);
      setNotes(data.notes || "");
      setSigOptions(data.fom_signature_options || []);
      const f = data.fom || {};
      setFom({
        fom_approved_labor: !!f.fom_approved_labor,
        fom_confirmed_mileage: !!f.fom_confirmed_mileage,
        fom_signature: f.fom_signature || "",
        miles_driven: f.miles_driven || "",
        materials_cost: f.materials_cost || "",
        mileage_cost: f.mileage_cost || "",
        lodging_cost: f.lodging_cost || "",
        additional_labor_cost: f.additional_labor_cost || "",
      });
      setSubmittedBy(data.submitted_by || f.fom_signature || (data.header || {}).fom_assigned || "");
      setRecipients(data.recipients || []);
      setSelectedRecIds(data.finance_recipient_ids || []);
      setSentInfo(data.sent_to_finance_at ? { at: data.sent_to_finance_at, to: data.sent_to_finance || [] } : null);
      setAttachments(data.attachments || []);
      setError(null);
    } catch (e) {
      setError(e?.response?.data?.detail || "Unable to load the Labor Bill.");
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => {
    load();
  }, [load]);

  // Layout responsivo (coluna única) com pinça/zoom habilitada no celular.
  useEffect(() => {
    const vp = document.querySelector('meta[name="viewport"]');
    const prev = vp ? vp.getAttribute("content") : null;
    if (vp) vp.setAttribute("content", "width=device-width, initial-scale=1, maximum-scale=5, user-scalable=yes, viewport-fit=cover");
    return () => { if (vp && prev != null) vp.setAttribute("content", prev); };
  }, []);

  const setQty = useCallback((key, val) => {
    const n = val === "" ? 0 : Math.max(0, parseInt(val, 10) || 0);
    const upd = (sections) =>
      sections.map((s) => ({ ...s, items: s.items.map((it) => (it.key === key ? { ...it, qty: n } : it)) }));
    setLeft((p) => upd(p));
    setRight((p) => upd(p));
  }, []);

  const catalogTotal = useMemo(() => {
    let t = 0;
    [...left, ...right].forEach((s) => s.items.forEach((it) => { t += (Number(it.qty) || 0) * (Number(it.unit_price) || 0); }));
    return t;
  }, [left, right]);

  // Labor Total = itens + "Additional Labor Cost" (o extra oficial, explicado nas Notes)
  const additionalLabor = parseMoney(fom.additional_labor_cost);
  const grandTotal = catalogTotal + additionalLabor;

  const clearForm = () => {
    if (!window.confirm("Clear all quantities, charges and notes on this form? (Nothing is saved until you Save / Submit.)")) return;
    const zero = (sections) => sections.map((sec) => ({ ...sec, items: sec.items.map((it) => ({ ...it, qty: 0 })) }));
    setLeft((p) => zero(p));
    setRight((p) => zero(p));
    setNotes("");
    setFom({ fom_approved_labor: false, fom_confirmed_mileage: false, fom_signature: "", miles_driven: "", materials_cost: "", mileage_cost: "", lodging_cost: "", additional_labor_cost: "" });
    toast.info("Form cleared.");
  };

  const buildBody = () => {
    const items = [];
    [...left, ...right].forEach((s) => s.items.forEach((it) => items.push({ description: it.description, qty: Number(it.qty) || 0 })));
    return {
      items,
      extra_items: [], // Extra Labor não existe no Labor Bill oficial (legado já convertido em Additional Labor Cost + Notes)
      notes,
      fom_approved_labor: fom.fom_approved_labor,
      fom_confirmed_mileage: fom.fom_confirmed_mileage,
      fom_signature: fom.fom_signature,
      miles_driven: fom.miles_driven,
      materials_cost: fom.materials_cost,
      mileage_cost: fom.mileage_cost,
      lodging_cost: fom.lodging_cost,
      additional_labor_cost: fom.additional_labor_cost,
      fom_submitted_by: submittedBy,
    };
  };

  const save = async () => {
    setSaving(true);
    try {
      await axios.put(`${API}/public/fom-bill/${token}`, buildBody());
      toast.success("Changes saved!");
      return true;
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Error saving.");
      return false;
    } finally {
      setSaving(false);
    }
  };

  const finishAndGenerate = async () => {
    const ok = await save();
    if (!ok) return;
    setSaving(true);
    try {
      const { data } = await axios.get(`${API}/public/fom-bill/${token}/pdf`);
      const bytes = atob(data.pdf_data);
      const arr = new Uint8Array(bytes.length);
      for (let i = 0; i < bytes.length; i++) arr[i] = bytes.charCodeAt(i);
      const url = URL.createObjectURL(new Blob([arr], { type: "application/pdf" }));
      setPdfBlobUrl(url);
      setPdfName(data.filename || "Labor Bill.pdf");
      setSentTo(null);
      setDone(true);
      toast.success("PDF generated!");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Error generating the PDF.");
    } finally {
      setSaving(false);
    }
  };

  const downloadPdf = () => {
    if (!pdfBlobUrl) return;
    const a = document.createElement("a");
    a.href = pdfBlobUrl;
    a.download = pdfName;
    document.body.appendChild(a);
    a.click();
    a.remove();
  };

  const toggleRec = (id) => setSelectedRecIds((p) => (p.includes(id) ? p.filter((x) => x !== id) : [...p, id]));
  const dndSensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { delay: 2000, tolerance: 8 } }),
    useSensor(TouchSensor, { activationConstraint: { delay: 2000, tolerance: 8 } }),
  );
  const onRecipientsDragEnd = (event) => {
    const { active, over } = event;
    if (!over || active.id === over.id) return;
    setRecipients((items) => {
      const oldIndex = items.findIndex((i) => i.id === active.id);
      const newIndex = items.findIndex((i) => i.id === over.id);
      if (oldIndex < 0 || newIndex < 0) return items;
      const next = arrayMove(items, oldIndex, newIndex);
      axios
        .post(`${API}/public/fom-bill/${token}/recipients/reorder`, { ordered_ids: next.map((i) => i.id) })
        .then(() => toast.success("Order saved!"))
        .catch(() => toast.error("Could not save the new order."));
      return next;
    });
  };
  const isValidEmail = (e) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test((e || "").trim());

  const addExtraEmail = () => {
    const e = (newEmail || "").trim();
    if (!isValidEmail(e)) return toast.error("Invalid email");
    if (extraEmails.includes(e) || recipients.some((r) => r.email === e)) {
      toast.info("Email already in the list");
      setNewEmail("");
      return;
    }
    setExtraEmails((p) => [...p, e]);
    setNewEmail("");
  };

  const saveNewEmail = async () => {
    const e = (newEmail || "").trim();
    if (!isValidEmail(e)) return toast.error("Invalid email");
    try {
      const { data } = await axios.post(`${API}/public/fom-bill/${token}/recipient`, { email: e });
      setRecipients((p) => (p.some((r) => r.id === data.id) ? p : [...p, data]));
      setSelectedRecIds((p) => (p.includes(data.id) ? p : [...p, data.id]));
      setNewEmail("");
      toast.success(data.already ? "Email already saved — selected." : "Email saved to the list!");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Error saving email.");
    }
  };

  const openSend = async () => {
    const ok = await save();
    if (ok) setShowSend(true);
  };

  const onPickAttachments = async (e) => {
    const files = Array.from(e.target.files || []);
    e.target.value = "";
    if (!files.length) return;
    setUploadingAttach(true);
    try {
      for (const f of files) {
        const blob = await compressImage(f);
        let fname = f.name || "receipt";
        if (blob !== f) fname = fname.replace(/\.(png|webp|heic|heif|jpeg|jpg)$/i, "") + ".jpg";
        const fd = new FormData();
        fd.append("file", blob, fname);
        const { data } = await axios.post(`${API}/public/fom-bill/${token}/attachment`, fd, {
          headers: { "Content-Type": "multipart/form-data" },
        });
        setAttachments((p) => [...p, data]);
      }
      toast.success("Receipt attached!");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Error uploading the file.");
    } finally {
      setUploadingAttach(false);
    }
  };

  const removeAttachment = async (aid) => {
    try {
      await axios.delete(`${API}/public/fom-bill/${token}/attachment/${aid}`);
      setAttachments((p) => p.filter((a) => a.id !== aid));
    } catch (err) {
      toast.error("Error removing the attachment.");
    }
  };

  const attUrl = (aid) => `${API}/public/fom-bill/${token}/attachment/${aid}/content`;
  const isImg = (a) => (a.content_type || "").startsWith("image/");
  const imageAttachments = attachments.filter(isImg);
  const lightboxIndex = lightbox ? imageAttachments.findIndex((a) => a.id === lightbox.id) : -1;
  const showLightboxAt = (i) => {
    if (i >= 0 && i < imageAttachments.length) setLightbox(imageAttachments[i]);
  };
  const openPreview = (a) => {
    if (isImg(a)) setLightbox(a);
    else window.open(attUrl(a.id), "_blank");
  };
  useEffect(() => {
    if (!lightbox) return;
    const onKey = (e) => {
      if (e.key === "Escape") setLightbox(null);
      else if (e.key === "ArrowRight") showLightboxAt(lightboxIndex + 1);
      else if (e.key === "ArrowLeft") showLightboxAt(lightboxIndex - 1);
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [lightbox, lightboxIndex]);

  const sendToFinance = async () => {
    if (selectedRecIds.length === 0 && extraEmails.length === 0) {
      return toast.error("Select at least one Finance email.");
    }
    setSending(true);
    try {
      const { data } = await axios.post(`${API}/public/fom-bill/${token}/send`, {
        recipient_ids: selectedRecIds,
        extra_emails: extraEmails,
      });
      setSentTo(data.sent_to || []);
      setShowSend(false);
      try {
        const pdfRes = await axios.get(`${API}/public/fom-bill/${token}/pdf`);
        const bytes = atob(pdfRes.data.pdf_data);
        const arr = new Uint8Array(bytes.length);
        for (let i = 0; i < bytes.length; i++) arr[i] = bytes.charCodeAt(i);
        const url = URL.createObjectURL(new Blob([arr], { type: "application/pdf" }));
        setPdfBlobUrl(url);
        setPdfName(pdfRes.data.filename || "Labor Bill.pdf");
      } catch (_e) { /* optional */ }
      setDone(true);
      toast.success("Sent to chosen e-mail!");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Error sending to Finance.");
    } finally {
      setSending(false);
    }
  };

  const chargeInput = (k) => (
    <input
      type="number"
      inputMode={k === "miles_driven" ? "numeric" : "decimal"}
      min="0"
      step={k === "miles_driven" ? "1" : "0.01"}
      value={fom[k]}
      onChange={(e) => setFom((p) => ({ ...p, [k]: e.target.value }))}
      style={inputStyle}
      data-testid={`fom-${k.replace(/_/g, "-")}`}
    />
  );

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center" style={{ background: C.bg, fontFamily: FONT }}>
        <div className="flex items-center gap-3" style={{ color: C.muted }} data-testid="fom-loading">
          <Loader2 className="h-6 w-6 animate-spin" />
          <span>Loading Labor Bill…</span>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen flex items-center justify-center p-6" style={{ background: C.bg, fontFamily: FONT }}>
        <div className="max-w-md text-center bg-white rounded-lg p-8" style={{ border: `1px solid ${C.border}` }} data-testid="fom-error">
          <AlertTriangle className="h-10 w-10 text-amber-500 mx-auto mb-3" />
          <h1 className="text-lg font-semibold" style={{ color: C.text }}>Link unavailable</h1>
          <p className="text-sm mt-2" style={{ color: C.muted }}>{error}</p>
        </div>
      </div>
    );
  }

  if (done) {
    return (
      <div className="min-h-screen flex items-center justify-center p-6" style={{ background: C.bg, fontFamily: FONT }}>
        <Toaster position="top-center" richColors />
        <div className="max-w-lg w-full text-center bg-white rounded-lg p-8" style={{ border: `1px solid ${C.border}` }} data-testid="fom-done">
          <CheckCircle2 className="h-14 w-14 mx-auto mb-4" style={{ color: C.green }} />
          <h1 className="text-xl font-bold" style={{ color: C.text }}>{sentTo ? "Sent to chosen e-mail!" : "Labor Bill ready!"}</h1>
          <p className="text-sm mt-2" style={{ color: C.muted }}>{header.job_name} · Job# {header.order_number}</p>
          <div className="mt-4 inline-flex items-baseline gap-2 rounded-lg px-5 py-3" style={{ background: C.head }}>
            <span className="text-sm" style={{ color: C.green }}>Labor Total</span>
            <span className="text-2xl font-bold tabular-nums" style={{ color: C.dark }}>{money(grandTotal)}</span>
          </div>
          {sentTo && sentTo.length > 0 && (
            <div className="mt-4 text-left rounded-lg p-3" style={{ background: C.bg }} data-testid="fom-sent-list">
              <p className="text-[11px] uppercase mb-1" style={{ color: C.muted }}>Sent to</p>
              {sentTo.map((e) => (
                <p key={e} className="text-[13px] flex items-center gap-2" style={{ color: C.text }}>
                  <Send className="h-3.5 w-3.5" style={{ color: C.green }} /> {e}
                </p>
              ))}
            </div>
          )}
          <p className="text-sm mt-5" style={{ color: C.muted }}>
            {sentTo
              ? "A copy was sent to the Admin. You can download the final PDF below or reopen the link to edit."
              : "Download the PDF. You can reopen this link to edit again."}
          </p>
          <div className="mt-5 flex flex-row flex-wrap gap-2 justify-center">
            {pdfBlobUrl && (
              <button onClick={downloadPdf} style={btnDark} data-testid="fom-download-pdf-btn">
                <Download className="h-4 w-4" /> Download PDF
              </button>
            )}
            {pdfBlobUrl && (
              <a href={pdfBlobUrl} target="_blank" rel="noreferrer" style={btnGrey} data-testid="fom-open-pdf-btn">
                <ExternalLink className="h-4 w-4" /> Open PDF
              </a>
            )}
            <button onClick={() => { setDone(false); setSentTo(null); }} style={{ ...btnGrey, background: "transparent", color: C.muted }} data-testid="fom-back-edit-btn">
              Back to edit
            </button>
          </div>
        </div>
      </div>
    );
  }

  const approvedLabel = (v) => (v ? "yes" : "pending");
  const sections = [...left, ...right];

  return (
    <div className="fom-page" style={{ background: C.bg, minHeight: "100vh", padding: "18px 10px 48px", fontFamily: FONT, color: C.text }}>
      <Toaster position="top-center" richColors />
      <style>{`
        .fom-page input, .fom-page select, .fom-page textarea, .fom-page button { font-family: ${FONT}; }
        .fom-page input:focus, .fom-page select:focus, .fom-page textarea:focus { outline: 2px solid ${C.green}; outline-offset: -1px; }
        .fom-page .fom-grid3 { display: grid; grid-template-columns: repeat(3, minmax(0, 1fr)); gap: 14px 16px; }
        .fom-page .fom-totals { display: grid; grid-template-columns: repeat(3, minmax(0, 1fr)); }
        .fom-page .fom-totals > div + div { border-left: 1px solid ${C.border}; }
        .fom-page .fom-actions { display: flex; align-items: center; justify-content: space-between; gap: 10px; flex-wrap: wrap; }
        .fom-page table.fom-items { width: 100%; border-collapse: collapse; table-layout: fixed; }
        .fom-page table.fom-items col.c-rate { width: 110px; } .fom-page table.fom-items col.c-qty { width: 130px; }
        .fom-page table.fom-items col.c-total { width: 110px; } .fom-page table.fom-items col.c-del { width: 44px; }
        .fom-page .fom-topbar { display: flex; align-items: center; justify-content: space-between; gap: 12px; }
        .fom-page table.fom-items th { background: ${C.head}; color: #374640; font-weight: 700; font-size: 12px; padding: 9px 16px; text-align: right; border-bottom: 1px solid ${C.border}; }
        .fom-page table.fom-items th:first-child { text-align: left; }
        .fom-page table.fom-items td { padding: 8px 16px; font-size: 13px; border-bottom: 1px solid ${C.line}; text-align: right; vertical-align: middle; }
        .fom-page table.fom-items td:first-child { text-align: left; }
        .fom-page table.fom-items tr:last-child td { border-bottom: none; }
        .fom-page table.fom-items td.fom-total { font-weight: 700; }
        @media (max-width: 640px) {
          .fom-page .fom-grid3 { grid-template-columns: 1fr; }
          .fom-page .fom-topbar { flex-direction: column; gap: 4px; text-align: center; padding: 12px 16px; }
          .fom-page .fom-topbar h1 { font-size: 16px; }
          .fom-page .fom-totals > div > div:first-child { font-size: 11px; white-space: nowrap; letter-spacing: 0; }
          .fom-page table.fom-items th, .fom-page table.fom-items td { padding-left: 8px; padding-right: 8px; font-size: 12px; }
          .fom-page table.fom-items col.c-rate { width: 68px; } .fom-page table.fom-items col.c-qty { width: 78px; }
          .fom-page table.fom-items col.c-total { width: 78px; } .fom-page table.fom-items col.c-del { width: 30px; }
          .fom-page table.fom-items td input { padding-left: 4px; padding-right: 2px; }
          .fom-page .fom-actions-right { width: 100%; justify-content: flex-end; }
        }
      `}</style>

      <div style={{ maxWidth: 920, margin: "0 auto" }}>
        {/* Top bar */}
        <div className="fom-topbar" style={{ background: C.dark, color: "#fff", borderRadius: 8, padding: "16px 24px", marginBottom: 12 }} data-testid="fom-topbar">
          <span style={{ fontWeight: 700, fontSize: 13, whiteSpace: "nowrap" }}>Renewal by Andersen Atlanta</span>
          <h1 style={{ margin: 0, fontWeight: 700, fontSize: 18, textAlign: "center", flex: 1 }}>Install Labor Bill</h1>
          <span style={{ fontSize: 13, opacity: 0.9, whiteSpace: "nowrap" }}>Installer Submission</span>
        </div>

        {sentInfo && (
          <div data-testid="fom-sent-banner" style={{ display: "flex", alignItems: "flex-start", gap: 8, background: "#fff", border: `1px solid ${C.green}`, borderRadius: 6, padding: "10px 14px", marginBottom: 12 }}>
            <CheckCircle2 style={{ height: 18, width: 18, color: C.green, flexShrink: 0, marginTop: 1 }} />
            <div style={{ fontSize: 13, lineHeight: 1.35, color: C.dark }}>
              <strong>Already sent</strong> on {fmtSentAt(sentInfo.at)}
              {sentInfo.to && sentInfo.to.length > 0 ? <> to {sentInfo.to.join(", ")}</> : null}.
              <span style={{ display: "block", color: C.muted, fontSize: 12 }}>Sending again will deliver another copy.</span>
            </div>
          </div>
        )}

        {/* JOB INFORMATION */}
        <Card testid="fom-job-info">
          <SectionBar title="Job Information" />
          <div style={{ padding: "14px 16px 16px" }}>
            <div className="fom-grid3">
              <Field label="Job Name"><ReadOnly value={header.job_name} testid="fom-hdr-job-name" /></Field>
              <Field label="Job #"><ReadOnly value={header.order_number} testid="fom-hdr-job-number" /></Field>
              <Field label="Date Completed"><ReadOnly value={fmtDate(header.install_date)} testid="fom-hdr-date" /></Field>
              <Field label="Crew Name"><ReadOnly value={header.crew_name} placeholder="Select Crew" testid="fom-hdr-crew" /></Field>
              <Field label="FOM Assigned"><ReadOnly value={header.fom_assigned} placeholder="Select FOM" testid="fom-hdr-fom-assigned" /></Field>
              <Field label="FOM Email"><ReadOnly value={header.fom_email} placeholder="Auto-filled" testid="fom-hdr-fom-email" /></Field>
            </div>
          </div>
          <div className="fom-totals" style={{ borderTop: `1px solid ${C.border}` }}>
            <Total label="Total Windows" value={header.total_windows ?? 0} testid="fom-total-windows" />
            <Total label="Total Doors" value={header.total_doors ?? 0} testid="fom-total-doors" />
            <Total label="Labor Total" value={money(grandTotal)} testid="fom-grand-total" />
          </div>
        </Card>

        {/* ITEM SECTIONS (single column: template order, left then right) */}
        {sections.map((s, i) => (
          <ItemSection key={`S${i}`} title={s.title} items={s.items} onQty={setQty} />
        ))}

        {/* ADDITIONAL JOB RELATED CHARGES */}
        <Card testid="fom-additional-charges">
          <SectionBar title="Additional Job Related Charges" />
          <div style={{ padding: "14px 16px 16px" }}>
            <div className="fom-grid3">
              <Field label="Materials Cost" sub="Prior FOM approval + receipt">{chargeInput("materials_cost")}</Field>
              <Field label="Mileage Cost" sub="Use instead of Trip Charge when applicable">{chargeInput("mileage_cost")}</Field>
              <Field label="Number Miles Driven">{chargeInput("miles_driven")}</Field>
              <Field label="Lodging / Food Cost" sub="Prior FOM approval + receipt">{chargeInput("lodging_cost")}</Field>
              <Field label="Additional Labor Cost" sub="Prior FOM approval">{chargeInput("additional_labor_cost")}</Field>
            </div>
            {legacyConverted && (
              <p style={{ margin: "10px 0 0", fontSize: 12, color: C.muted }} data-testid="fom-legacy-converted-note">
                Extra labor lines from the installer were moved into <b>Additional Labor Cost</b> and listed in the Notes.
              </p>
            )}
            {/* Receipts / photos attached to this Labor Bill */}
            <div style={{ marginTop: 16, paddingTop: 14, borderTop: `1px solid ${C.line}`, display: "flex", alignItems: "center", gap: 10, flexWrap: "wrap" }} data-testid="fom-footer-thumb-strip">
              <label style={{ ...btnGrey, padding: "8px 14px", fontSize: 12, cursor: "pointer", position: "relative", opacity: uploadingAttach || saving || sending ? 0.6 : 1, pointerEvents: uploadingAttach || saving || sending ? "none" : "auto" }} title="Attach receipt / photo" data-testid="fom-footer-attach-btn">
                {uploadingAttach ? <Loader2 className="h-4 w-4 animate-spin" /> : <Paperclip className="h-4 w-4" />}
                {uploadingAttach ? "Uploading…" : "Attach receipt / photo"}
                {attachments.length > 0 && (
                  <span style={{ position: "absolute", top: -7, right: -7, minWidth: 18, height: 18, padding: "0 5px", borderRadius: 9, background: C.dark, color: "#fff", fontSize: 11, fontWeight: 700, display: "flex", alignItems: "center", justifyContent: "center" }} data-testid="fom-footer-attach-count">{attachments.length}</span>
                )}
                <input type="file" accept="image/*,application/pdf" multiple className="hidden" onChange={onPickAttachments} disabled={uploadingAttach || saving || sending} data-testid="fom-footer-attach-input" />
              </label>
              {attachments.length === 0 && <span style={{ fontSize: 12, color: C.muted }}>Receipts are sent together with the Labor Bill PDF.</span>}
              {attachments.map((a) => (
                <div key={a.id} style={{ position: "relative", flexShrink: 0 }} data-testid={`fom-footer-thumb-${a.id}`}>
                  <a href={attUrl(a.id)} target="_blank" rel="noreferrer" title={a.filename} onClick={(e) => { e.preventDefault(); openPreview(a); }}>
                    {isImg(a) ? (
                      <img src={attUrl(a.id)} alt={a.filename} style={{ height: 48, width: 48, borderRadius: 6, objectFit: "cover", border: `1px solid ${C.border}`, background: "#fff", cursor: "zoom-in" }} />
                    ) : (
                      <span style={{ height: 48, width: 48, borderRadius: 6, border: `1px solid ${C.border}`, background: "#fff", display: "flex", alignItems: "center", justifyContent: "center" }}><FileText className="h-5 w-5" style={{ color: C.muted }} /></span>
                    )}
                  </a>
                  <button onClick={() => removeAttachment(a.id)} style={{ position: "absolute", top: -6, right: -6, height: 20, width: 20, borderRadius: 10, background: "#fff", border: `1px solid ${C.inputBorder}`, color: C.muted, display: "flex", alignItems: "center", justifyContent: "center", cursor: "pointer" }} title="Remove" data-testid={`fom-footer-thumb-remove-${a.id}`}><X className="h-3 w-3" /></button>
                </div>
              ))}
            </div>
          </div>
        </Card>

        {/* NOTES */}
        <Card>
          <SectionBar title="Notes" />
          <div style={{ padding: 16 }}>
            <textarea
              rows={5}
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              placeholder="Enter job notes, explanations, or approval details..."
              style={{ ...inputStyle, height: "auto", minHeight: 130, padding: 12, resize: "vertical", lineHeight: 1.4 }}
              data-testid="fom-notes"
            />
          </div>
        </Card>

        {/* FOM REVIEW + ACTIONS */}
        <Card testid="fom-review-card">
          <div style={{ padding: "16px 16px 14px" }}>
            <div className="fom-grid3">
              <Field label="FOM Approved Labor?">
                <select value={approvedLabel(fom.fom_approved_labor)} onChange={(e) => setFom((p) => ({ ...p, fom_approved_labor: e.target.value === "yes" }))} style={selectStyle} data-testid="fom-approved-labor">
                  <option value="pending">Pending FOM Review</option>
                  <option value="yes">Yes — Approved</option>
                </select>
              </Field>
              <Field label="FOM Confirmed Mileage?">
                <select value={approvedLabel(fom.fom_confirmed_mileage)} onChange={(e) => setFom((p) => ({ ...p, fom_confirmed_mileage: e.target.value === "yes" }))} style={selectStyle} data-testid="fom-confirmed-mileage">
                  <option value="pending">Pending FOM Review</option>
                  <option value="yes">Yes — Confirmed</option>
                </select>
              </Field>
              <Field label="FOM Signature">
                <select value={fom.fom_signature} onChange={(e) => setFom((p) => ({ ...p, fom_signature: e.target.value }))} style={{ ...selectStyle, color: fom.fom_signature ? C.text : C.disabledText }} data-testid="fom-signature-select">
                  <option value="">Completed during FOM review</option>
                  {sigOptions.map((o) => (<option key={o} value={o}>{o}</option>))}
                </select>
              </Field>
            </div>

            <div className="fom-actions" style={{ marginTop: 18 }}>
              <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
                <button onClick={clearForm} disabled={saving || sending} style={btnGrey} data-testid="fom-clear-btn">Clear Form</button>
                <button onClick={save} disabled={saving || sending} style={btnGrey} data-testid="fom-save-btn">
                  {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />} Save
                </button>
              </div>
              <div className="fom-actions-right" style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
                <button onClick={finishAndGenerate} disabled={saving || sending} style={btnGrey} data-testid="fom-finish-btn">Print / Save PDF</button>
                <button onClick={openSend} disabled={saving || sending} style={btnDark} data-testid="fom-send-finance-btn">
                  {saving || sending ? <Loader2 className="h-4 w-4 animate-spin" /> : null} Submit to FOM
                </button>
              </div>
            </div>
            <p style={{ margin: "12px 0 0", fontSize: 12, lineHeight: 1.4, color: C.muted }} data-testid="fom-submit-hint">
              Submitting validates the labor bill and sends it to the assigned FOM for review and approval. If the submission service is unavailable, you'll be given the option to send it by email instead.
            </p>
          </div>
        </Card>
      </div>

      {/* Modal: Send to Finance */}
      {showSend && (
        <div className="fixed inset-0 z-40 flex items-end sm:items-center justify-center bg-black/40" data-testid="fom-send-modal" style={{ fontFamily: FONT }}>
          <div className="bg-white w-full sm:max-w-lg sm:rounded-xl rounded-t-xl max-h-[90vh] overflow-y-auto shadow-xl">
            <div className="sticky top-0 px-5 py-3 flex items-center justify-between" style={{ background: C.dark }}>
              <h3 className="text-white font-bold flex items-center gap-2"><Send className="h-4 w-4" /> Submit Labor Bill</h3>
              <button onClick={() => setShowSend(false)} className="text-white/80 hover:text-white" data-testid="fom-send-close"><X className="h-5 w-5" /></button>
            </div>
            <div className="p-5 space-y-4">
              <p className="text-[13px]" style={{ color: C.muted }}>Select the email(s). Finance is pre-selected. A copy is sent to the Admin automatically.</p>
              <div className="space-y-2">
                {recipients.length === 0 && <p className="text-[13px]" style={{ color: C.muted }}>No saved emails. Add one below.</p>}
                {recipients.length > 1 && (
                  <p className="text-[11px]" style={{ color: C.muted }}>Tip: hold the ⠿ handle for 2s and drag to reorder.</p>
                )}
                <DndContext sensors={dndSensors} collisionDetection={closestCenter} onDragEnd={onRecipientsDragEnd}>
                  <SortableContext items={recipients.map((r) => r.id)} strategy={verticalListSortingStrategy}>
                    <div className="space-y-2">
                      {recipients.map((r) => (
                        <SortableRecipient key={r.id} r={r} checked={selectedRecIds.includes(r.id)} onToggle={() => toggleRec(r.id)} />
                      ))}
                    </div>
                  </SortableContext>
                </DndContext>
              </div>
              {extraEmails.length > 0 && (
                <div className="flex flex-wrap gap-2">
                  {extraEmails.map((e) => (
                    <span key={e} className="inline-flex items-center gap-1 text-[12px] rounded-full px-3 py-1" style={{ background: C.head, color: C.text }} data-testid={`fom-extra-chip-${e}`}>
                      <Mail className="h-3 w-3" style={{ color: C.muted }} />{e}
                      <button onClick={() => setExtraEmails((p) => p.filter((x) => x !== e))} className="hover:text-red-500" style={{ color: C.muted }}><X className="h-3 w-3" /></button>
                    </span>
                  ))}
                </div>
              )}
              <div className="pt-3" style={{ borderTop: `1px solid ${C.line}` }}>
                <label className="text-[11px] uppercase" style={{ color: C.muted }}>Add another email</label>
                <div className="mt-1 flex items-center gap-2">
                  <input value={newEmail} onChange={(e) => setNewEmail(e.target.value)} type="email" inputMode="email" placeholder="email@example.com" className="flex-1 min-w-0 text-[13px] rounded-md px-2 py-2" style={{ border: `1px solid ${C.inputBorder}` }} data-testid="fom-new-email" />
                  <button onClick={addExtraEmail} className="text-[12px] rounded-md px-3 py-2 whitespace-nowrap" style={{ border: `1px solid ${C.inputBorder}`, color: C.text }} data-testid="fom-add-extra-btn">Add</button>
                  <button onClick={saveNewEmail} title="Save this email to the list" className="rounded-md text-white p-2" style={{ background: C.dark }} data-testid="fom-save-email-btn"><Plus className="h-4 w-4" /></button>
                </div>
                <p className="text-[11px] mt-1" style={{ color: C.muted }}>“Add” sends this time only · “+” saves the email for next time.</p>
              </div>

              <div className="pt-3" style={{ borderTop: `1px solid ${C.line}` }}>
                <label className="text-[11px] uppercase" style={{ color: C.muted }}>Attach receipts / photos (optional)</label>
                {attachments.length > 0 && (
                  <div className="mt-2 space-y-1.5" data-testid="fom-attachment-list">
                    {attachments.map((a) => (
                      <div key={a.id} className="flex items-center gap-2 rounded-md px-2 py-1.5" style={{ border: `1px solid ${C.border}`, background: C.bg }} data-testid={`fom-attachment-${a.id}`}>
                        <a href={attUrl(a.id)} target="_blank" rel="noreferrer" className="shrink-0" title="Open" onClick={(e) => { e.preventDefault(); openPreview(a); }}>
                          {isImg(a) ? (
                            <img src={attUrl(a.id)} alt={a.filename} className="h-11 w-11 rounded object-cover bg-white cursor-zoom-in" style={{ border: `1px solid ${C.border}` }} data-testid={`fom-attachment-thumb-${a.id}`} />
                          ) : (
                            <span className="h-11 w-11 rounded bg-white flex items-center justify-center" style={{ border: `1px solid ${C.border}` }} data-testid={`fom-attachment-thumb-${a.id}`}><FileText className="h-5 w-5" style={{ color: C.muted }} /></span>
                          )}
                        </a>
                        <span className="flex-1 min-w-0 truncate text-[12px]" style={{ color: C.text }}>{a.filename}</span>
                        <span className="text-[11px] tabular-nums" style={{ color: C.muted }}>{fmtSize(a.size)}</span>
                        <button onClick={() => removeAttachment(a.id)} className="hover:text-red-600 p-0.5" style={{ color: C.muted }} title="Remove" data-testid={`fom-attachment-remove-${a.id}`}><X className="h-4 w-4" /></button>
                      </div>
                    ))}
                  </div>
                )}
                <label className="mt-2 inline-flex items-center gap-2 rounded-md px-3 py-2 text-[12px] cursor-pointer" style={{ border: `1px solid ${C.inputBorder}`, color: C.text }} data-testid="fom-attachment-add-btn">
                  {uploadingAttach ? <Loader2 className="h-4 w-4 animate-spin" /> : <Paperclip className="h-4 w-4" />}
                  {uploadingAttach ? "Uploading…" : "Add photo / receipt"}
                  <input type="file" accept="image/*,application/pdf" multiple className="hidden" onChange={onPickAttachments} disabled={uploadingAttach} data-testid="fom-attachment-input" />
                </label>
                <p className="text-[11px] mt-1" style={{ color: C.muted }}>These files will be sent together with the Labor Bill PDF. Up to 8 files, max 12MB each.</p>
              </div>
            </div>
            <div className="sticky bottom-0 bg-white px-5 py-3 flex items-center justify-end gap-2" style={{ borderTop: `1px solid ${C.line}` }}>
              <button onClick={() => setShowSend(false)} style={{ ...btnGrey, background: "transparent", color: C.muted }} data-testid="fom-send-cancel">Cancel</button>
              <button onClick={sendToFinance} disabled={sending} style={btnDark} data-testid="fom-send-confirm">
                {sending ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />} Send
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Lightbox: enlarged photo preview with gallery navigation */}
      {lightbox && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/85 p-4" data-testid="fom-lightbox" onClick={() => setLightbox(null)} style={{ fontFamily: FONT }}>
          <button className="absolute top-3 right-3 text-white/90 hover:text-white" onClick={() => setLightbox(null)} data-testid="fom-lightbox-close" aria-label="Close"><X className="h-8 w-8" /></button>
          {imageAttachments.length > 1 && lightboxIndex > 0 && (
            <button className="absolute left-2 sm:left-4 top-1/2 -translate-y-1/2 h-11 w-11 rounded-full bg-white/15 hover:bg-white/30 text-white flex items-center justify-center" onClick={(e) => { e.stopPropagation(); showLightboxAt(lightboxIndex - 1); }} data-testid="fom-lightbox-prev" aria-label="Previous"><ChevronLeft className="h-7 w-7" /></button>
          )}
          {imageAttachments.length > 1 && lightboxIndex < imageAttachments.length - 1 && (
            <button className="absolute right-2 sm:right-4 top-1/2 -translate-y-1/2 h-11 w-11 rounded-full bg-white/15 hover:bg-white/30 text-white flex items-center justify-center" onClick={(e) => { e.stopPropagation(); showLightboxAt(lightboxIndex + 1); }} data-testid="fom-lightbox-next" aria-label="Next"><ChevronRight className="h-7 w-7" /></button>
          )}
          <div className="max-w-full max-h-full flex flex-col items-center" onClick={(e) => e.stopPropagation()}>
            <img src={attUrl(lightbox.id)} alt={lightbox.filename} className="max-w-full max-h-[82vh] object-contain rounded-lg shadow-2xl" data-testid="fom-lightbox-img" />
            <p className="mt-3 text-white/80 text-[13px] truncate max-w-[90vw]">
              {lightbox.filename}
              {imageAttachments.length > 1 && <span className="text-white/50" data-testid="fom-lightbox-counter"> · {lightboxIndex + 1} / {imageAttachments.length}</span>}
            </p>
          </div>
        </div>
      )}
    </div>
  );
}
